import React from 'react';
import { Modal, message } from 'antd';

export const showBulkActionFeedback = (result, options = {}) => {
  const {
    entityLabel = '记录',
    successVerb = '处理',
    retryLabel = '仅重试失败项',
    onRetry,
    selectLabel = '重新选中失败项',
    onSelectFailed
  } = options;

  const failedIds = Array.isArray(result?.failedIds) ? result.failedIds : [];
  const failures = Array.isArray(result?.failures) ? result.failures : [];
  const normalizedFailures = failures.length > 0
    ? failures
    : failedIds.map((id) => ({ id, reason: '未返回详细原因' }));
  const successCount = Number(result?.successCount || 0);

  if (failedIds.length === 0) {
    message.success(`${result?.message || '批量操作成功'}，成功 ${successCount} 条`);
    return;
  }

  message.warning(`${result?.message || '批量操作部分失败'}，成功 ${successCount} 条，失败 ${failedIds.length} 条`);

  const failedIdsText = failedIds.join(', ');
  const failureLines = normalizedFailures.map((item) => `${item.id}: ${item.reason || '未返回详细原因'}`);
  let modalRef;
  modalRef = Modal.warning({
    title: `${entityLabel}批量${successVerb}结果`,
    width: 620,
    okText: '知道了',
    content: (
      <div style={{ display: 'grid', gap: '12px' }}>
        <div style={summaryStyle}>
          已成功 {successCount} 条，失败 {failedIds.length} 条。失败 ID 如下：
        </div>
        <div style={listStyle}>
          {failureLines.map((line) => (
            <div key={line} style={failureLineStyle}>{line}</div>
          ))}
        </div>
        <div style={actionRowStyle}>
          <button
            type="button"
            onClick={() => copyFailedIds(failedIdsText)}
            style={buttonStyle('neutral')}
          >
            复制失败 ID
          </button>
          <button
            type="button"
            onClick={() => exportFailedIds(entityLabel, normalizedFailures)}
            style={buttonStyle('neutral')}
          >
            导出失败清单
          </button>
          {typeof onSelectFailed === 'function' && (
            <button
              type="button"
              onClick={() => {
                onSelectFailed(failedIds);
                message.success('失败项已重新选中');
              }}
              style={buttonStyle('neutral')}
            >
              {selectLabel}
            </button>
          )}
          {typeof onRetry === 'function' && (
            <button
              type="button"
              onClick={() => {
                modalRef?.destroy();
                onRetry(failedIds);
              }}
              style={buttonStyle('primary')}
            >
              {retryLabel}
            </button>
          )}
        </div>
        <div style={noteStyle}>
          常见原因包括状态已变更、数据不存在，或该记录不满足当前批量操作条件。
        </div>
      </div>
    )
  });
};

const summaryStyle = {
  color: '#1f2a44',
  lineHeight: 1.7
};

const listStyle = {
  maxHeight: '180px',
  overflowY: 'auto',
  padding: '12px 14px',
  borderRadius: '14px',
  background: '#f8fbff',
  border: '1px solid #dbeafe',
  color: '#334155',
  lineHeight: 1.7,
  wordBreak: 'break-all'
};

const failureLineStyle = {
  lineHeight: 1.7
};

const actionRowStyle = {
  display: 'flex',
  gap: '8px',
  flexWrap: 'wrap'
};

const noteStyle = {
  color: '#8c5a12',
  background: '#fff7e6',
  border: '1px solid #ffd591',
  borderRadius: '14px',
  padding: '10px 12px',
  lineHeight: 1.6,
  fontSize: '13px'
};

const buttonStyle = (variant = 'neutral') => {
  const variants = {
    neutral: {
      background: '#f3f6fb',
      color: '#1f2a44',
      boxShadow: 'inset 0 0 0 1px rgba(148, 163, 184, 0.22)'
    },
    primary: {
      background: 'linear-gradient(135deg, #1677ff 0%, #2457d6 100%)',
      color: '#ffffff',
      boxShadow: '0 10px 20px rgba(22, 119, 255, 0.22)'
    }
  };
  const style = variants[variant] || variants.neutral;
  return {
    padding: '7px 14px',
    background: style.background,
    color: style.color,
    border: 'none',
    borderRadius: '999px',
    boxShadow: style.boxShadow,
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600
  };
};

const copyFailedIds = async (value) => {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      message.success('失败 ID 已复制');
      return;
    }
  } catch (_) {
    // Fallback below.
  }

  try {
    const textArea = document.createElement('textarea');
    textArea.value = value;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    message.success('失败 ID 已复制');
  } catch (_) {
    message.error('复制失败，请手动复制');
  }
};

const exportFailedIds = (entityLabel, failures) => {
  try {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const content = [
      `${entityLabel}批量操作失败清单`,
      `生成时间: ${new Date().toLocaleString()}`,
      '',
      ...failures.map((item) => `${item.id}: ${item.reason || '未返回详细原因'}`)
    ].join('\n');
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${entityLabel}-failed-ids-${timestamp}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    message.success('失败清单已导出');
  } catch (_) {
    message.error('导出失败，请稍后重试');
  }
};
